import type { PropsElement } from '../props';
import type { ToolFn, ToolSpec } from '../types';

export const describeSpec: ToolSpec = {
  name: 'describe_model',
  description:
    'What the model contains: storeys, element types with counts, material names with counts, property sets with their property names, base quantity names, units. Call this first when you are unsure which type, material or property a question refers to.',
  input_schema: { type: 'object', properties: {} },
};

export const describe =
  (elements: PropsElement[]): ToolFn =>
  () => {
    const storeys = new Map<string, number>();
    const types = new Map<string, number>();
    const materials = new Map<string, number>();
    const psets = new Map<string, Map<string, number>>();
    const quantities = new Map<string, Set<string>>();
    let withVolume = 0;
    for (const e of elements) {
      bump(storeys, e.storey ?? '–');
      bump(types, e.type);
      for (const n of e.material?.names ?? []) bump(materials, n);
      for (const [set, props] of Object.entries(e.psets ?? {})) {
        const m = psets.get(set) ?? new Map<string, number>();
        for (const k of Object.keys(props)) bump(m, k);
        psets.set(set, m);
      }
      for (const [set, q] of Object.entries(e.quantities ?? {})) {
        const s = quantities.get(set) ?? new Set<string>();
        for (const k of Object.keys(q)) s.add(k);
        quantities.set(set, s);
      }
      if (e.quantities || e.derived) withVolume++;
    }
    return {
      elements: elements.length,
      units: { length: 'm', area: 'm2', volume: 'm3' },
      storeys: top(storeys, 30),
      types: top(types, 40),
      materials: top(materials, 40),
      property_sets: Object.fromEntries(
        [...psets.entries()]
          .sort((a, b) => b[1].size - a[1].size)
          .slice(0, 20)
          .map(([set, m]) => [set, top(m, 12).map((x) => x.name)]),
      ),
      quantity_sets: Object.fromEntries([...quantities.entries()].map(([k, v]) => [k, [...v]])),
      elements_with_volume: withVolume,
      fields:
        'Filter/sort/aggregate fields for query_elements: type, name, storey, material, volume, area, q:<QuantityName>, p:<PropertySet>.<Property>',
    };
  };

function bump(m: Map<string, number>, k: string) {
  m.set(k, (m.get(k) ?? 0) + 1);
}

function top(m: Map<string, number>, n: number): Array<{ name: string; count: number }> {
  return [...m.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, count]) => ({ name, count }));
}
